import React, { useState, useCallback, useRef, useEffect } from "react";
import {
  Modal,
  Alert,
  View,
  Text,
  Pressable,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  useWindowDimensions,
  Platform,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";
import DualPhotoCapture from "./DualPhotoCapture";
import BeRealCheckInScreen from "./beRealCheckIn/BeRealCheckInScreen";
import { uploadImageUriToConvex } from "../../utils/convexImageUpload";
import { useMomentUpload } from "../../utils/MomentUploadContext";

export default function BeRealCheckIn({
  visible,
  restaurant,
  userPrivyDid,
  userId,
  checkInLocation,
  onComplete,
  onCancel,
  /**
   * Wide desktop web: start with a dialog (skip harsh fullscreen camera),
   * centered review modal, and return to the dialog from the camera.
   */
  desktopWebOptimized = false,
  /**
   * When true (e.g. map desktop sidebar), render the whole flow inline with no Modal
   * so it stays inside the parent column.
   */
  embeddedInSidebar = false,
}) {
  const desktopUIMode = desktopWebOptimized || embeddedInSidebar;
  const { height: windowHeight } = useWindowDimensions();
  const resolvedWindowHeight = Number.isFinite(windowHeight) && windowHeight > 0 ? windowHeight : 800;
  const [step, setStep] = useState(() => (desktopUIMode ? "gate" : "capture"));
  const [frontPhotoUri, setFrontPhotoUri] = useState(null);
  const [backPhotoUri, setBackPhotoUri] = useState(null);
  const [caption, setCaption] = useState("");
  const [selectedVisibility, setSelectedVisibility] = useState("friends");
  const [isSubmitting, setIsSubmitting] = useState(false);
  /** Captured at Share tap so upload survives parent unmount after onComplete() */
  const uploadSnapshotRef = useRef(null);

  const createMoment = useMutation(api.moments.createMoment);
  const generateUploadUrl = useMutation(api.images.generateUploadUrl);
  const checkInWithLocation = useMutation(api.checkInWithLocation.checkInWithLocation);
  const { startUpload, updateProgress, completeUpload, failUpload } = useMomentUpload();

  const hasCheckedInToday = useQuery(
    api.checkInWithLocation.hasCheckedInToday,
    userId && restaurant?._id ? { userId, restaurantId: restaurant._id } : "skip"
  );

  useEffect(() => {
    if (!visible) {
      setStep(desktopUIMode ? "gate" : "capture");
      setFrontPhotoUri(null);
      setBackPhotoUri(null);
      setCaption("");
      setSelectedVisibility("friends");
      setIsSubmitting(false);
    }
  }, [visible, desktopUIMode]);

  const resetToCapture = () => {
    setStep(desktopUIMode ? "gate" : "capture");
    setFrontPhotoUri(null);
    setBackPhotoUri(null);
    setCaption("");
    setSelectedVisibility("friends");
    setIsSubmitting(false);
  };

  const handleDone = (front, back, captionText) => {
    setFrontPhotoUri(front);
    setBackPhotoUri(back);
    setCaption(captionText || "");
    setStep("review");
  };

  const handleSkipWithoutPhotos = () => {
    setFrontPhotoUri(null);
    setBackPhotoUri(null);
    setCaption("");
    setStep("review");
  };

  const handleCaptureCancel = useCallback(() => {
    if (desktopUIMode) {
      setStep("gate");
      return;
    }
    onCancel();
  }, [desktopUIMode, onCancel]);

  const uploadPhoto = useCallback(
    async (photoUri, onProgress) => {
      try {
        const uploadUrl = await generateUploadUrl({ privyDid: userPrivyDid });
        if (!uploadUrl) throw new Error("Failed to get upload URL");
        return await uploadImageUriToConvex(uploadUrl, photoUri, { onProgress });
      } catch (e) {
        console.error("Error uploading photo:", e);
        return null;
      }
    },
    [generateUploadUrl, userPrivyDid]
  );

  const performUpload = useCallback(async () => {
    const snap = uploadSnapshotRef.current;
    if (!snap) {
      console.warn("BeReal performUpload: no snapshot");
      return;
    }
    const {
      frontPhotoUri: fUri,
      backPhotoUri: bUri,
      selectedVisibility: vis,
      caption: cap,
      restaurant: rest,
      checkInLocation: loc,
      userId: uid,
      hasCheckedInToday: alreadyIn,
    } = snap;

    try {
      let frontPhotoId;
      let backPhotoId;

      if (fUri && bUri) {
        const frontId = await uploadPhoto(fUri, (p) => {
          updateProgress(Math.floor(p * 0.4));
        });
        const backId = await uploadPhoto(bUri, (p) => {
          updateProgress(40 + Math.floor(p * 0.4));
        });
        if (!frontId || !backId) throw new Error("Failed to upload photos");
        frontPhotoId = frontId;
        backPhotoId = backId;
        updateProgress(80);
      } else {
        updateProgress(20);
      }

      await createMoment({
        privyDid: userPrivyDid,
        restaurantId: rest._id,
        caption: cap || undefined,
        visibility: vis,
        frontPhotoId,
        backPhotoId,
        ...(loc
          ? { checkInLatitude: loc.latitude, checkInLongitude: loc.longitude }
          : {}),
      });

      if (uid && rest?._id && loc) {
        if (alreadyIn === true) {
          console.warn("User already checked in today, skipping check-in mutation");
        } else {
          try {
            const checkInResult = await checkInWithLocation({
              userId: uid,
              restaurantId: rest._id,
              latitude: loc.latitude,
              longitude: loc.longitude,
            });
            if (!checkInResult.success) {
              if (
                checkInResult.errorMessage?.includes("already checked in") ||
                checkInResult.errorMessage?.includes("recently checked in")
              ) {
                Alert.alert(
                  "Already Checked In ⏰",
                  `You've already checked in to ${rest?.name || "this restaurant"} today.\n\nYour moment was still shared!`,
                  [{ text: "Got it!", style: "default" }]
                );
              } else {
                console.error("Failed to record check-in:", checkInResult.errorMessage);
              }
            }
          } catch (e) {
            console.error("Error recording check-in:", e);
          }
        }
      }

      updateProgress(100);
      completeUpload();
    } catch (error) {
      console.error("Error submitting BeReal moment:", error);
      failUpload(error instanceof Error ? error : new Error(String(error)));
    } finally {
      setIsSubmitting(false);
    }
  }, [
    uploadPhoto,
    updateProgress,
    createMoment,
    userPrivyDid,
    checkInWithLocation,
    completeUpload,
    failUpload,
  ]);

  const submitMomentWithVisibility = useCallback(
    (visibility) => {
      if (hasCheckedInToday === true) {
        Alert.alert(
          "Already Checked In ⏰",
          `You've already checked in to ${restaurant?.name || "this restaurant"} today.`,
          [{ text: "Got it!", style: "default" }]
        );
        return;
      }

      setSelectedVisibility(visibility);
      uploadSnapshotRef.current = {
        frontPhotoUri,
        backPhotoUri,
        selectedVisibility: visibility,
        caption: caption.trim(),
        restaurant,
        checkInLocation,
        userId,
        hasCheckedInToday,
      };

      setIsSubmitting(true);
      startUpload({
        restaurantName: restaurant?.name,
        restaurantId: restaurant?._id,
        retryCallback: performUpload,
      });
      onComplete();
      performUpload();
    },
    [
      hasCheckedInToday,
      restaurant,
      frontPhotoUri,
      backPhotoUri,
      caption,
      checkInLocation,
      userId,
      startUpload,
      performUpload,
      onComplete,
    ]
  );

  if (!visible || !restaurant || !userPrivyDid) {
    return null;
  }

  const gateCard = (
    <View style={[gateStyles.card, embeddedInSidebar && gateStyles.cardEmbedded]}>
      <View style={gateStyles.iconWrap}>
        <Ionicons name="sparkles" size={28} color="#6B4C3B" />
      </View>
      <Text style={gateStyles.kicker}>Check-in</Text>
      <Text style={gateStyles.title} numberOfLines={2}>
        {restaurant.name}
      </Text>
      <Text style={gateStyles.body}>
        {embeddedInSidebar
          ? "You're in range. Finish without photos, or add a quick dual photo with your webcam—your browser will ask for permission."
          : "You're in range. On desktop you can finish without opening the camera, or add a quick dual photo using your webcam—your browser will ask for permission."}
      </Text>
      <TouchableOpacity
        style={gateStyles.primaryBtn}
        onPress={handleSkipWithoutPhotos}
        activeOpacity={0.88}
      >
        <Text style={gateStyles.primaryBtnText}>Continue — no photos</Text>
      </TouchableOpacity>
      <TouchableOpacity
        style={gateStyles.secondaryBtn}
        onPress={() => setStep("capture")}
        activeOpacity={0.88}
      >
        <Ionicons name="camera-outline" size={20} color="#6B4C3B" style={{ marginRight: 8 }} />
        <Text style={gateStyles.secondaryBtnText}>Use webcam — add photos</Text>
      </TouchableOpacity>
      <TouchableOpacity onPress={onCancel} style={gateStyles.cancelBtn} hitSlop={{ top: 8, bottom: 8 }}>
        <Text style={gateStyles.cancelText}>Cancel</Text>
      </TouchableOpacity>
    </View>
  );

  if (desktopUIMode && step === "gate") {
    if (embeddedInSidebar) {
      return (
        <View style={gateStyles.embeddedRoot}>
          <ScrollView
            style={gateStyles.embeddedGateScroll}
            contentContainerStyle={gateStyles.embeddedGateScrollContent}
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator={false}
          >
            {gateCard}
          </ScrollView>
        </View>
      );
    }
    return (
      <Modal visible={visible} transparent animationType="fade" onRequestClose={onCancel}>
        <View style={gateStyles.modalRoot}>
        <View style={gateStyles.overlay}>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Close check-in"
            onPress={onCancel}
            style={StyleSheet.absoluteFillObject}
          />
          {gateCard}
        </View>
        </View>
      </Modal>
    );
  }

  if (step === "capture") {
    const captureScreen = (
      <DualPhotoCapture
        onDone={handleDone}
        onCancel={handleCaptureCancel}
        onSkipWithoutPhotos={handleSkipWithoutPhotos}
        countdownDuration={3}
      />
    );

    if (embeddedInSidebar) {
      return (
        <View style={[gateStyles.embeddedRoot, gateStyles.embeddedCaptureRoot]}>
          <View style={gateStyles.embeddedCaptureInner}>{captureScreen}</View>
        </View>
      );
    }

    return (
      <Modal
        visible={visible}
        animationType={desktopUIMode ? "fade" : "slide"}
        presentationStyle={desktopUIMode ? "overFullScreen" : "fullScreen"}
        transparent={desktopUIMode}
        onRequestClose={handleCaptureCancel}
      >
        {desktopUIMode ? (
          <View style={gateStyles.modalRoot}>
            <View style={gateStyles.captureOverlay}>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Close dual photo capture"
                onPress={handleCaptureCancel}
                style={StyleSheet.absoluteFillObject}
              />
              <View
                style={[
                  gateStyles.captureInner,
                  {
                    maxHeight: Math.round(resolvedWindowHeight * 0.92),
                    zIndex: 1,
                  },
                ]}
              >
                {captureScreen}
              </View>
            </View>
          </View>
        ) : (
          captureScreen
        )}
      </Modal>
    );
  }

  const reviewScreen = (
    <BeRealCheckInScreen
      restaurant={restaurant}
      frontPhotoUri={frontPhotoUri}
      backPhotoUri={backPhotoUri}
      selectedVisibility={selectedVisibility}
      isSubmitting={isSubmitting}
      onBack={resetToCapture}
      onSelectVisibility={submitMomentWithVisibility}
      desktopWebLayout={desktopUIMode}
    />
  );

  if (embeddedInSidebar) {
    return (
      <View style={[gateStyles.embeddedRoot, gateStyles.embeddedReviewRoot]}>
        {Platform.OS === "web" ? (
          <View style={gateStyles.embeddedReviewWebScroll}>{reviewScreen}</View>
        ) : (
          <ScrollView
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator
            style={gateStyles.embeddedReviewNativeScroll}
            contentContainerStyle={gateStyles.reviewScrollContent}
          >
            {reviewScreen}
          </ScrollView>
        )}
      </View>
    );
  }

  return (
    <Modal
      visible={visible}
      animationType={desktopUIMode ? "fade" : "slide"}
      presentationStyle={desktopUIMode ? "overFullScreen" : "fullScreen"}
      transparent={desktopUIMode}
      onRequestClose={onCancel}
    >
      {desktopUIMode ? (
        <View style={gateStyles.modalRoot}>
          <View style={gateStyles.reviewOverlay}>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Dismiss"
              onPress={onCancel}
              style={StyleSheet.absoluteFillObject}
            />
            <View
              style={[
                gateStyles.reviewInner,
                {
                  maxHeight: Math.round(resolvedWindowHeight * 0.92),
                  minHeight: Math.min(320, resolvedWindowHeight * 0.4),
                  zIndex: 1,
                },
              ]}
            >
              {/*
                RN Web: ScrollView inside flex-centered modals often measures 0 height.
                Use a plain View + overflow:auto so the polaroid lays out with intrinsic height.
              */}
              {Platform.OS === "web" ? (
                <View
                  style={[
                    gateStyles.reviewWebScroll,
                    { maxHeight: Math.round(resolvedWindowHeight * 0.88) },
                  ]}
                >
                  {reviewScreen}
                </View>
              ) : (
                <ScrollView
                  keyboardShouldPersistTaps="handled"
                  showsVerticalScrollIndicator
                  style={{ width: "100%", flex: 1, minHeight: 400 }}
                  contentContainerStyle={gateStyles.reviewScrollContent}
                >
                  {reviewScreen}
                </ScrollView>
              )}
            </View>
          </View>
        </View>
      ) : (
        reviewScreen
      )}
    </Modal>
  );
}

const gateStyles = StyleSheet.create({
  /** Sidebar / inline host: fill column, no Modal. */
  embeddedRoot: {
    flex: 1,
    width: "100%",
    minHeight: 0,
  },
  embeddedGateScroll: {
    flex: 1,
  },
  embeddedGateScrollContent: {
    padding: 16,
    paddingBottom: 24,
    flexGrow: 1,
    justifyContent: "center",
  },
  embeddedCaptureRoot: {
    backgroundColor: "#000000",
  },
  embeddedCaptureInner: {
    flex: 1,
    width: "100%",
    minHeight: 0,
  },
  embeddedReviewRoot: {
    backgroundColor: "#F5F1ED",
  },
  embeddedReviewWebScroll: {
    flex: 1,
    width: "100%",
    minHeight: 0,
    overflow: "auto",
  },
  embeddedReviewNativeScroll: {
    width: "100%",
    flex: 1,
  },
  /** Ensures RN Web Modal has a sized subtree (avoids empty / 0-height modals). */
  modalRoot: {
    flex: 1,
    width: "100%",
  },
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.5)",
    justifyContent: "center",
    alignItems: "center",
    padding: 24,
  },
  card: {
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    padding: 24,
    maxWidth: 480,
    width: "100%",
    zIndex: 1,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.15,
    shadowRadius: 24,
    elevation: 12,
  },
  cardEmbedded: {
    maxWidth: "100%",
    alignSelf: "stretch",
  },
  iconWrap: {
    alignSelf: "center",
    marginBottom: 8,
  },
  kicker: {
    fontSize: 13,
    fontWeight: "600",
    color: "#6B4C3B",
    letterSpacing: 0.5,
    textTransform: "uppercase",
    textAlign: "center",
    marginBottom: 4,
  },
  title: {
    fontSize: 22,
    fontWeight: "700",
    color: "#1a1a1a",
    textAlign: "center",
    marginBottom: 12,
  },
  body: {
    fontSize: 15,
    lineHeight: 22,
    color: "#4b5563",
    textAlign: "center",
    marginBottom: 20,
  },
  primaryBtn: {
    backgroundColor: "#6B4C3B",
    borderRadius: 12,
    paddingVertical: 14,
    alignItems: "center",
    marginBottom: 10,
  },
  primaryBtnText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "700",
  },
  secondaryBtn: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 2,
    borderColor: "#D4C4B8",
    borderRadius: 12,
    paddingVertical: 12,
    marginBottom: 12,
  },
  secondaryBtnText: {
    color: "#6B4C3B",
    fontSize: 15,
    fontWeight: "600",
  },
  cancelBtn: {
    alignSelf: "center",
    paddingVertical: 8,
  },
  cancelText: {
    fontSize: 15,
    color: "#6b7280",
    fontWeight: "500",
  },
  reviewOverlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.45)",
    justifyContent: "center",
    alignItems: "stretch",
    paddingHorizontal: 20,
    paddingVertical: 24,
  },
  captureOverlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.62)",
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 20,
    paddingVertical: 24,
  },
  captureInner: {
    width: "100%",
    maxWidth: 540,
    minWidth: 280,
    minHeight: 420,
    borderRadius: 18,
    overflow: "hidden",
    backgroundColor: "#000000",
  },
  reviewInner: {
    maxWidth: 540,
    width: "100%",
    minWidth: 0,
    alignSelf: "center",
    backgroundColor: "#F5F1ED",
    borderRadius: 16,
    overflow: "hidden",
  },
  /** Web: scroll container with real layout height (not RN ScrollView flex bugs). */
  reviewWebScroll: {
    width: "100%",
    minHeight: 280,
    overflow: "auto",
  },
  reviewScrollContent: {
    paddingBottom: 24,
  },
});
